import Joi from 'joi';
import { get, isEmpty } from 'lodash';
import {
  ExpenseVATRegime,
  IExpenseItemV2,
  ReviewStatusEnum,
} from 'types/expenses.types';
import { VATReturnFrequencyEnum, VATTypeEnum } from 'types/users.types';
import { isExpert, maximumAmortizationPeriod } from 'utils/constants';
import { round } from 'utils/math';
import { formatErrorMessage } from 'validation/commonValidators';
import type useFormMeta from './useFormMeta';

const datePattern = /^\d{4}-(0[1-9]|1[012])-(0[1-9]|[12][0-9]|3[01])$/;

const itemValidationSchema = Joi.object({
  category: Joi.object()
    .required()
    .messages({ '*': 'expenseItem.categoryId.any.required' }),
  professionalPart: Joi.number().min(0.01).max(1).required().messages({
    'number.max': 'expense.validator.professionalPart.higher',
    'number.min': 'expense.validator.professionalPart.lower',
    'number.base': 'expense.validator.professionalPart.missing',
  }),
  currencyAmount: Joi.when('$currencyAmount.isApplicable', {
    is: true,
    then: Joi.when('$isImportVATForm', {
      is: true,
      then: Joi.number()
        .min(0)
        .custom((value, helpers) => {
          const items = (helpers.prefs.context?.items ||
            []) as Partial<IExpenseItemV2>[];

          const allItemsHaveAmount = (items || []).every(
            (item) => typeof item.currencyAmount === 'number',
          );

          if (!allItemsHaveAmount) return value;

          const total = (items || []).reduce(
            (acc: number, item) => acc + (item.currencyAmount || 0),
            0,
          );

          if (total >= 0.01) return value;

          return helpers.error('total.number.min');
        })
        .required()
        .messages({
          'total.number.min': 'expenseItem.amount.number.total_number_min',
          '*': 'expenseItem.amount.number.min_0',
        }),
      otherwise: Joi.number()
        .min(0.01)
        .required()
        .messages({ '*': 'expenseItem.amount.number.min' }),
    }),
  }),
  VATRegime: Joi.when('$VATRegime.isApplicable', {
    is: true,
    then: Joi.valid(...Object.values(ExpenseVATRegime))
      .required()
      .messages({ '*': 'expenseItem.VATRegime.any.required' }),
  }),

  VATRate: Joi.when('$VATRate.isApplicable', {
    is: true,
    then: Joi.when('VATRegime', {
      is: ExpenseVATRegime.foreign_vat,
      then: Joi.equal(null),
      otherwise: Joi.number()
        .min(0)
        .required()
        .messages({ '*': 'expenseItem.VATRate.any.required' }),
    }),
  }),
  localVATRate: Joi.when('$localVATRate.isApplicable', {
    is: true,
    then: Joi.number()
      .required()
      .messages(formatErrorMessage('expenseItem', 'localVATRate')),
  }),
  name: Joi.when('$name.isApplicable', {
    is: true,
    then: Joi.string()
      .required()
      .messages({ '*': 'expenseItem.name.any.required' }),
    otherwise: Joi.allow(null),
  }),
  isAsset: Joi.when('$isAsset.isApplicable', {
    is: true,
    then: Joi.allow(true).required(),
    otherwise: Joi.allow(false, null),
  }).default(false),
  amortizationPeriod: Joi.when('$amortizationPeriod.isApplicable', {
    is: true,
    then: Joi.alternatives([
      Joi.valid(null),
      Joi.number()
        .integer()
        .custom((value, helpers) => {
          const years = value / 12;
          if (years === 0) return value;
          if (years > maximumAmortizationPeriod || years < 1) {
            return helpers.error(
              'expense.validator.item.amortization.period.incorrect_temp',
            );
          }
          return value;
        })
        .required(),
    ]).messages({
      '*': 'expense.validator.item.amortization.period.incorrect_temp',
    }),
  }),
  vehicle: Joi.when('$vehicle.isApplicable', {
    is: true,
    then: Joi.object()
      .required()
      .messages({ '*': 'expenseItem.vehicleId.any.required' }),
    otherwise: Joi.allow(null),
  }),
  maxDeductibleVAT: isExpert
    ? Joi.number()
        .min(0)
        .max(1)
        .required()
        .messages(formatErrorMessage('expenseItem', 'maxDeductibleVAT'))
    : Joi.any(),
  incomeTaxDeductibility: isExpert
    ? Joi.number()
        .min(0)
        .max(1.2)
        .required()
        .messages(formatErrorMessage('expenseItem', 'incomeTaxDeductibility'))
    : Joi.any(),
  // -----------
  tripType: Joi.when('$tripType.isApplicable', {
    is: true,
    then: Joi.string().required(),
  }).messages({
    '*': 'expenseItem.tripType.string.empty',
  }),
  tripVehicleType: Joi.when('$tripVehicleType.isApplicable', {
    is: true,
    then: Joi.string().required(),
  }).messages({
    '*': 'expenseItem.tripVehicleType.string.empty',
  }),
  tripTotalKm: Joi.when('$tripTotalKm.isApplicable', {
    is: true,
    then: Joi.number().required().min(1).max(9999),
  }).messages({
    'number.min': 'expenseItem.tripTotalKm.number.min',
    'number.max': 'expenseItem.tripTotalKm.number.max',
    '*': 'expenseItem.tripTotalKm.number.empty',
  }),
  tripWorkingDays: Joi.when('$tripWorkingDays.isApplicable', {
    is: true,
    then: Joi.number().integer().required().min(1).max(365),
  }).messages({
    'number.min': 'expenseItem.tripWorkingDays.number.min',
    'number.max': 'expenseItem.tripWorkingDays.number.max',
    'number.integer': 'expenseItem.tripWorkingDays.number.integer',
    '*': 'expenseItem.tripWorkingDays.number.empty',
  }),
}).required();

export const generalValidationSchema = Joi.object({
  isInvoice: Joi.boolean().default(false),
  isCreditNote: Joi.boolean().default(false),
  expenseDate: Joi.string().trim().pattern(datePattern).required().messages({
    '*': 'expenseItem.expenseDate.string.pattern.empty',
  }),
  supplier: Joi.when('$supplier.isApplicable', {
    is: true,
    then: Joi.object({
      name: Joi.string()
        .required()
        .messages({ '*': 'expense.supplier.name.string.empty' }),
    }).required(),
  }),
  currencyRate: Joi.when('$currencyRate.isApplicable', {
    is: true,
    then: Joi.number()
      .custom((value, helpers) => {
        const items = get(helpers.state, [
          'ancestors',
          0,
          'items',
        ]) as Partial<IExpenseItemV2>[];
        const amountInclVAT = (items || []).reduce(
          (acc: number, item) => acc + (item.currencyAmount || 0),
          0,
        );
        const paymentAmount = round(amountInclVAT / value);
        if (Number.isNaN(paymentAmount) || paymentAmount < 0.01) {
          return helpers.error('number.min');
        }
        return value;
      })
      .required()
      .messages({
        'number.min': 'invoice.paymentAmount.number.min',
        '*': 'invoice.paymentAmount.any.required',
      }),
  }),
  items: Joi.array().min(1).required(),
  payments: Joi.object({
    cash: Joi.boolean().default(false),
    other_accounts: Joi.boolean().default(false),
    transactions: Joi.array().default([]),
  }),
  period: Joi.when('$period.isApplicable', {
    is: true,
    then: Joi.object({
      year: Joi.number()
        .integer()
        .max(new Date().getFullYear() + 1)
        .required()
        .messages({ 'number.max': 'invoice.periodYear.number.max' }),
      quarter: Joi.number()
        .integer()
        .min(1)
        .max(4)
        .when('/user.VATReturnFrequency', {
          is: VATReturnFrequencyEnum.quarterly,
          then: Joi.required().messages({
            '*': 'invoice.periodQuarter.any.invalid',
          }),
        }),
      month: Joi.number()
        .integer()
        .min(1)
        .max(12)
        .when('/user.VATReturnFrequency', {
          is: VATReturnFrequencyEnum.monthly,
          then: Joi.required().messages({
            '*': 'invoice.periodMonth.any.invalid',
          }),
        }),
    })
      .required()
      .messages({ '*': 'expense.period.object.empty' }),
  }),
  user: Joi.object({
    VATType: Joi.valid(...Object.values(VATTypeEnum)).required(),
    VATReturnFrequency: Joi.valid(
      ...Object.values(VATReturnFrequencyEnum),
    ).required(),
  }).required(),
  // customFeesAmount: Joi.number().min(0).default(0),
  accountantReview: Joi.object({
    reviewStatus: Joi.valid(...Object.values(ReviewStatusEnum)).default(
      'not_reviewed',
    ),
  }),
  file: Joi.object({
    path: Joi.string().allow(null),
  }).allow(null),
  isSuggestedTransactionActive: Joi.any(),
  // custom((value, helpers) => {
  //   if (value) return helpers.error('accept_or_reject');
  // }).messages({
  //   '*': 'expense.form.error.suggestedTransaction',
  // }),
});

export const getValidationSchema = ({
  getFormMeta,
  getItemMeta,
}: Pick<
  ReturnType<typeof useFormMeta>,
  'getFormMeta' | 'getItemMeta'
>): any => {
  return {
    validateAsync: (values: any, options: Joi.AsyncValidationOptions) => {
      const formMeta = getFormMeta(values);
      return Promise.allSettled([
        generalValidationSchema.validateAsync(values, {
          ...options,
          context: {
            ...options.context,
            ...formMeta.fields,
            ...formMeta.helpers,
          },
        }),
        Promise.allSettled(
          values.items?.map((item: any, i: number) => {
            const itemMeta = getItemMeta(values, item, i);
            return itemValidationSchema.validateAsync(item, {
              ...options,
              context: {
                ...options.context,
                ...formMeta.fields,
                ...formMeta.helpers,
                ...itemMeta.fields,
                ...itemMeta.helpers,
                items: values.items,
              },
            });
          }) || [],
        ),
      ]).then(([general, items]) => {
        let errors = { _original: values, details: [] };

        if (general.status === 'rejected') {
          errors = {
            ...errors,
            details: errors.details.concat(general.reason.details),
          };
        }

        (
          items as PromiseFulfilledResult<PromiseSettledResult<any>[]>
        ).value.map((item, index: number) => {
          if (item.status === 'rejected') {
            errors = {
              ...errors,
              details: errors.details.concat(
                item.reason.details.map((detail: any) => ({
                  ...detail,
                  path: ['items', index].concat(detail.path),
                })),
              ),
            };
          }
        });

        if (!isEmpty(errors.details)) return Promise.reject(errors);

        return {
          ...(general as PromiseFulfilledResult<any>).value,
          items: (
            items as PromiseFulfilledResult<PromiseFulfilledResult<any>[]>
          ).value.map((item) => item.value),
        };
      });
    },
  };
};
